// ============================================================================
// CLINICIAN DASHBOARD
//
// For specialist and gp_employee roles:
// - Shows real-time referral activity overview from Firebase
// - Referrals today (count of user's submissions today)
// - Last 7 days (count of user's submissions in last 7 days)
// - Urgent rate (percentage of urgent outcomes)
// - Not Yet count (incomplete referrals)
// - Quick actions: Start new referral, view submissions
//
// NOTE: Uses "Last 7 days" to match MySubmissionsView date filtering
//
// READS FROM:
// - tenants/{tenantId}/metrics (filtered by createdByUid)
// ============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import { safeUserError } from '../core/safeError.js';
import {
  Activity, Shield, AlertTriangle, Clock, RefreshCw,
  CheckCircle, ArrowRight, TrendingUp, FileText, Loader2
} from 'lucide-react';
import { useAuth } from '../auth/AuthContext.jsx';
import {
  getMetricsByUser,
  getMetricsBySpecialty,
  isSpecialist,
  isGpEmployee,
} from '../core/index.js';
import { useNotYetBadgeCount } from '../hooks/useNotYetBadgeCount.js';
import { RGCard, RGButton, RGAlertBanner } from '../components/rg/RGPrimitives.jsx';

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Get start of today (local time, midnight)
 */
const getStartOfToday = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
};

/**
 * Get start of "last 7 days" (local time)
 * This matches what MySubmissionsView uses for consistency
 */
const getStartOfLast7Days = () => {
  const now = new Date();
  const sevenDaysAgo = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7, 0, 0, 0, 0);
  return sevenDaysAgo;
};

/**
 * Convert Firestore timestamp or date to JS Date
 */
const toDate = (timestamp) => {
  if (!timestamp) return null;
  if (timestamp.toDate) return timestamp.toDate();
  if (timestamp instanceof Date) return timestamp;
  if (typeof timestamp === 'string') return new Date(timestamp);
  if (timestamp.seconds) return new Date(timestamp.seconds * 1000);
  return null;
};

// ============================================================================
// STAT CARD COMPONENT
// ============================================================================

const StatCard = ({ icon: Icon, label, value, subtitle, color = 'emerald', isLoading = false }) => {
  const colorClasses = {
    emerald: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400',
    amber: 'bg-amber-500/10 border-amber-500/30 text-amber-400',
    blue: 'bg-blue-500/10 border-blue-500/30 text-blue-400',
    red: 'bg-red-500/10 border-red-500/30 text-red-400',
    violet: 'bg-violet-500/10 border-violet-500/30 text-violet-400',
  };

  const iconColorClasses = {
    emerald: 'bg-emerald-500/20 text-emerald-400',
    amber: 'bg-amber-500/20 text-amber-400',
    blue: 'bg-blue-500/20 text-blue-400',
    red: 'bg-red-500/20 text-red-400',
    violet: 'bg-violet-500/20 text-violet-400',
  };

  return (
    <div className={`rounded-2xl border p-6 ${colorClasses[color]}`}>
      <div className="flex items-start justify-between mb-4">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${iconColorClasses[color]}`}>
          <Icon className="w-6 h-6" />
        </div>
      </div>
      <p className="text-3xl font-bold text-primary mb-1">
        {isLoading ? (
          <span className="inline-flex items-center gap-2">
            <Loader2 className="w-6 h-6 animate-spin text-slate-500" />
          </span>
        ) : (
          value
        )}
      </p>
      <p className="text-sm font-medium">{label}</p>
      {subtitle && <p className="text-xs text-slate-500 mt-1">{subtitle}</p>}
    </div>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const ClinicianDashboard = ({ user, onNavigate, activeFocusTarget }) => {
  const { tenant } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    today: 0,
    thisWeek: 0,
    urgentCount: 0,
    urgentRate: 0,
    notYetCount: 0,
    routineCount: 0,
    totalThisWeek: 0,
  });

  // Role check
  const userIsSpecialist = isSpecialist(user?.role);
  const userIsGpEmployee = isGpEmployee(user?.role);
  const roleColor = userIsSpecialist ? 'blue' : 'emerald';

  // Live Not Yet inbox count (same source as sidebar badge)
  const notYetInboxCount = useNotYetBadgeCount({
    role: user?.role,
    tenantId: user?.tenantId || tenant?.id || tenant?.tenantId,
    uid: user?.uid || user?.id,
  });

  // Load dashboard stats
  const loadStats = useCallback(async () => {
    // Support both uid and id fields (Firebase Auth uses uid, profile uses id)
    const userId = user?.uid || user?.id;

    if (!user?.tenantId || !userId) {
      console.log('[ClinicianDashboard] Missing tenantId or userId');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      console.log('[ClinicianDashboard] Loading stats for user:', userId, 'role:', user.role);

      let metrics = [];

      // Specialists see specialty-wide data, GP employees see only their own
      if (userIsSpecialist && user.specialtyIds?.length > 0) {
        // Fetch metrics for all of the specialist's specialties
        console.log('[ClinicianDashboard] Specialist - fetching specialty data for:', user.specialtyIds);

        const allMetrics = await Promise.all(
          user.specialtyIds.map(specialtyId =>
            getMetricsBySpecialty(user.tenantId, specialtyId, { pageSize: 100 })
          )
        );

        // Combine all specialty metrics
        metrics = allMetrics.flatMap(result => result.rows || []);
        console.log('[ClinicianDashboard] Specialist metrics loaded:', metrics.length);
      } else {
        // GP Employee - fetch only their own submissions
        console.log('[ClinicianDashboard] GP Employee - fetching own submissions');
        const result = await getMetricsByUser(user.tenantId, userId, {
          pageSize: 100,
        });
        metrics = result.rows || [];
        console.log('[ClinicianDashboard] GP Employee metrics loaded:', metrics.length);
      }

      // Calculate stats - use "Last 7 days" to match MySubmissionsView
      const startOfToday = getStartOfToday();
      const startOf7Days = getStartOfLast7Days();

      let today = 0;
      let last7Days = 0;
      let urgentCount = 0;
      let notYetCount = 0;
      let routineCount = 0;

      metrics.forEach((metric) => {
        const savedAt = toDate(metric.savedAt || metric.createdAt);
        if (!savedAt) return;

        // Last 7 days (matches MySubmissionsView "Last 7 days" filter)
        if (savedAt >= startOf7Days) {
          last7Days++;

          // Count by outcome
          const status = metric.outcomeStatus?.toLowerCase();
          if (status === 'urgent') urgentCount++;
          else if (status === 'not_yet') notYetCount++;
          else if (status === 'routine') routineCount++;

          // Today
          if (savedAt >= startOfToday) {
            today++;
          }
        }
      });

      // Calculate urgent rate
      const urgentRate = last7Days > 0 ? (urgentCount / last7Days) * 100 : 0;

      setStats({
        today,
        thisWeek: last7Days,
        urgentCount,
        urgentRate,
        notYetCount,
        routineCount,
        totalThisWeek: last7Days,
      });

      console.log('[ClinicianDashboard] Stats calculated:', { today, last7Days, urgentCount, routineCount, notYetCount, urgentRate });
    } catch (err) {
      console.error('[ClinicianDashboard] Error loading stats:', err);
      setError(safeUserError(err));
    } finally {
      setLoading(false);
    }
  }, [user?.tenantId, user?.uid, user?.id, user?.specialtyIds, userIsSpecialist]);

  // Load on mount
  useEffect(() => {
    loadStats();
  }, [loadStats]);

  // Handle navigation
  const handleStartReferral = () => {
    if (onNavigate) {
      onNavigate('gate');
    }
  };

  const handleViewSubmissions = () => {
    if (onNavigate) {
      if (userIsGpEmployee) {
        onNavigate('my-submissions');
      } else if (userIsSpecialist) {
        onNavigate('analytics');
      }
    }
  };

  // ── GP Employee: minimal dashboard — single centered action ─────────────────
  if (userIsGpEmployee) {
    const hasNotYet = notYetInboxCount > 0;
    const hl = (target) =>
      activeFocusTarget === target ? 'learning-focus-highlight' : '';

    return (
      <div
        className="relative flex flex-col items-center justify-center min-h-[calc(100vh-4rem)] gap-6"
      >
        {/* Action Required banner */}
        {hasNotYet && (
          <RGAlertBanner
            variant="warning"
            className={`absolute top-0 left-0 right-0 z-50 px-4 py-2 rounded-none border-x-0 border-t-0 bg-status-needs-info text-status-needs-info border-status-needs-info ${activeFocusTarget === 'action_required_alert' ? 'learning-focus-highlight' : ''}`}
            data-focus-target="action_required_alert"
          >
            <p className="text-center text-sm">
              <AlertTriangle className="w-4 h-4 inline-block mr-1.5 -mt-0.5" />
              <span className="font-semibold">Action Required:</span>{' '}
              You have {notYetInboxCount} referral{notYetInboxCount !== 1 ? 's' : ''} stalled waiting for missing information.
            </p>
          </RGAlertBanner>
        )}

        <h1 className="text-4xl md:text-5xl font-bold text-primary -mt-16 mb-[1em]">
          Hello, {user?.name || 'Provider'}!
        </h1>
        <div className="flex flex-col sm:flex-row items-center gap-4">
          <RGButton
            onClick={handleStartReferral}
            className={`inline-flex items-center gap-3 px-10 py-5 rounded-2xl text-lg shadow-xl shadow-emerald-500/20 ${hl('start_new_referral_button')}`}
            data-focus-target="start_new_referral_button"
          >
            <Shield className="w-6 h-6" />
            Start New Referral
          </RGButton>
          {hasNotYet && (
            <button
              onClick={() => onNavigate && onNavigate('not-yet-inbox')}
              className={`inline-flex items-center gap-3 px-10 py-5 bg-amber-500/15 hover:bg-amber-500/25 active:bg-amber-500/30 border border-amber-500/40 rounded-2xl text-amber-300 text-lg font-semibold transition-colors ${hl('resolve_referrals_button')}`}
              data-focus-target="resolve_referrals_button"
            >
              <Clock className="w-6 h-6" />
              Resolve Referrals
            </button>
          )}
        </div>
      </div>
    );
  }

  // ── Specialist: full activity dashboard ───────────────────────────────────
  return (
    <div className="p-4 md:p-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full bg-${roleColor}-500/10 border border-${roleColor}-500/20 mb-4`}>
            <TrendingUp className={`w-4 h-4 text-${roleColor}-400`} />
            <span className={`text-xs font-medium text-${roleColor}-400 uppercase`}>Specialist</span>
          </div>
          <h2 className="text-xl md:text-2xl font-semibold text-primary mb-2">
            Welcome back, {user?.name || 'Clinician'}
          </h2>
          <p className="text-slate-400">Specialty referral activity overview</p>
        </div>

        <button
          onClick={loadStats}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-slate-800/50 hover:bg-slate-800 border border-slate-700/50 rounded-xl text-slate-300 hover:text-white transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Error State */}
      {error && (
        <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-red-400" />
            <div>
              <p className="text-sm font-medium text-red-400">Error loading dashboard</p>
              <p className="text-xs text-red-400/70">{error}</p>
            </div>
          </div>
          <button
            onClick={loadStats}
            className="mt-3 flex items-center gap-2 px-3 py-1.5 bg-red-500/20 hover:bg-red-500/30 rounded-lg text-sm text-red-400"
          >
            <RefreshCw className="w-4 h-4" />
            Retry
          </button>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard
          icon={Activity}
          label="Referrals Today"
          value={loading ? '' : stats.today}
          subtitle="Submissions made today"
          color="emerald"
          isLoading={loading}
        />
        <StatCard
          icon={Shield}
          label="Last 7 Days"
          value={loading ? '' : stats.thisWeek}
          subtitle="Total last 7 days"
          color="blue"
          isLoading={loading}
        />
        <StatCard
          icon={AlertTriangle}
          label="Urgent Rate"
          value={loading ? '' : `${stats.urgentRate.toFixed(1)}%`}
          subtitle={`${stats.urgentCount} urgent referrals`}
          color="amber"
          isLoading={loading}
        />
        <StatCard
          icon={Clock}
          label="Not Yet"
          value={loading ? '' : stats.notYetCount}
          subtitle="Incomplete referrals"
          color="violet"
          isLoading={loading}
        />
      </div>

      {/* Breakdown Card */}
      <RGCard variant="muted" className="p-6 mb-8">
        <h3 className="text-lg font-semibold text-primary mb-4">Last 7 Days Breakdown</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <div>
            <p className={`text-3xl font-bold ${loading ? 'text-slate-500' : 'text-primary'}`}>
              {loading ? <Loader2 className="w-6 h-6 animate-spin" /> : stats.thisWeek}
            </p>
            <p className="text-sm text-slate-400">Total Referrals</p>
          </div>
          <div>
            <p className={`text-3xl font-bold ${loading ? 'text-slate-500' : 'text-emerald-400'}`}>
              {loading ? <Loader2 className="w-6 h-6 animate-spin" /> : stats.routineCount}
            </p>
            <p className="text-sm text-slate-400">Routine</p>
          </div>
          <div>
            <p className={`text-3xl font-bold ${loading ? 'text-slate-500' : 'text-red-400'}`}>
              {loading ? <Loader2 className="w-6 h-6 animate-spin" /> : stats.urgentCount}
            </p>
            <p className="text-sm text-slate-400">Urgent</p>
          </div>
          <div>
            <p className={`text-3xl font-bold ${loading ? 'text-slate-500' : 'text-amber-400'}`}>
              {loading ? <Loader2 className="w-6 h-6 animate-spin" /> : stats.notYetCount}
            </p>
            <p className="text-sm text-slate-400">Not Yet</p>
          </div>
        </div>
      </RGCard>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <button
          onClick={handleViewSubmissions}
          className={`flex items-center justify-between p-6 bg-${roleColor}-500/10 border border-${roleColor}-500/30 rounded-2xl hover:bg-${roleColor}-500/20 transition-colors group`}
        >
          <div className="flex items-center gap-4">
            <div className={`w-12 h-12 rounded-xl bg-${roleColor}-500/20 flex items-center justify-center`}>
              <FileText className={`w-6 h-6 text-${roleColor}-400`} />
            </div>
            <div className="text-left">
              <p className="font-semibold text-primary">View Analytics</p>
              <p className="text-sm text-slate-400">View specialty analytics</p>
            </div>
          </div>
          <ArrowRight className={`w-5 h-5 text-${roleColor}-400 group-hover:translate-x-1 transition-transform`} />
        </button>
      </div>

      {/* Empty state */}
      {!loading && stats.thisWeek === 0 && (
        <RGCard variant="muted" className="mt-8 p-8 text-center">
          <div className={`w-16 h-16 rounded-2xl bg-${roleColor}-500/20 flex items-center justify-center mx-auto mb-4`}>
            <Activity className={`w-8 h-8 text-${roleColor}-400`} />
          </div>
          <h3 className="text-lg font-semibold text-primary mb-2">No Activity Yet</h3>
          <p className="text-slate-400">
            No referrals in your specialty this week.
          </p>
        </RGCard>
      )}
    </div>
  );
};

export default ClinicianDashboard;
